import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    AccountBalanceOutlined,
    ReceiptLongOutlined,
    SyncOutlined,
    AdminPanelSettingsOutlined,
    DashboardOutlined,
    HourglassTopOutlined,
    ManageAccountsOutlined,
    PersonSearchOutlined,
    FiberNewOutlined,
    MonitorHeartOutlined,
    HomeOutlined,
    CampaignOutlined,
    BusinessOutlined,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import { isAccessManagementRole } from "components/RequirePermission/RequireAccessManagementRole";
import { useAccessPermissions, useCurrentUserRoleNames } from "components/RequirePermission/useAccessPermissions";
import { useMeteredUsageEnterpriseCapabilities } from "components/RequirePermission/useCapabilities";
import { useCanSeeMeteredUsage } from "pages/meteredUsage/meteredAccess";
import { useCanUseSync } from "./sync/syncAccess";
import EcrOpsPlatformSelector from "./components/EcrOpsPlatformSelector";

import "./EcrOpsLayout.css";

const ACCESS_MANAGEMENT_PATH = "/ecr-ops/access-management/role-assignments";

const NAV_ITEMS = [
    {
        label: "Home Dashboard",
        to: "/dashboard",
        icon: <HomeOutlined />,
    },
    {
        label: "OPS Homepage",
        to: "/ecr-ops",
        icon: <DashboardOutlined />,
        end: true,
    },
    {
        label: "Search",
        to: "/ecr-ops/search",
        icon: <PersonSearchOutlined />,
    },
    {
        label: "Org Management",
        to: "/ecr-ops/org-management",
        icon: <ManageAccountsOutlined />,
    },
    {
        label: "Access Management",
        to: ACCESS_MANAGEMENT_PATH,
        icon: <AdminPanelSettingsOutlined />,
    },
    {
        label: "New Organisations",
        to: "/ecr-ops/new-orgs",
        icon: <FiberNewOutlined />,
    },
    {
        label: "System Health",
        to: "/ecr-ops/health",
        icon: <MonitorHeartOutlined />,
    },
    {
        label: "Enterprise Usage",
        to: "/ecr-ops/copilot-enterprise",
        icon: <BusinessOutlined />,
    },
    {
        label: "Stuck Users",
        to: "/ecr-ops/stuck-users",
        icon: <HourglassTopOutlined />,
    },
    {
        label: "Metered Usage",
        to: "/ecr-ops/metered-usage",
        icon: <ReceiptLongOutlined />,
        needsMeteredUsage: true,
    },
    {
        label: "Sync Operations",
        to: "/ecr-ops/sync",
        icon: <SyncOutlined />,
        needsSync: true,
    },
    {
        label: "Cost Centers",
        to: "/ecr-ops/cost-centers",
        icon: <AccountBalanceOutlined />,
    },
    {
        label: "Portal Config",
        to: "/ecr-ops/portal-config",
        icon: <CampaignOutlined />,
    },
];

const EcrOpsLayout = () => {
    const [collapsed, setCollapsed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Access Management is restricted to Enterprise Admin / Operations Admin —
    // the route itself is guarded by RequireAccessManagementRole, but the sidebar
    // link must also be hidden for everyone else so they never see a route they'd
    // immediately get blocked from (loads once here, cached for the whole session).
    useAccessPermissions();
    const roleNames = useCurrentUserRoleNames();
    const canSeeAccessManagement = isAccessManagementRole(roleNames);

    // Enterprise metered usage has its own permissions (separate from the org-scoped tier), so the link only shows
    // for people who can actually open it.
    const { canViewSummary: canSeeMeteredUsagePermission } = useMeteredUsageEnterpriseCapabilities();
    // Temporarily limited to Enterprise Admins as well.
    const isMeteredUsageRole = useCanSeeMeteredUsage();
    const canSeeMeteredUsage = canSeeMeteredUsagePermission && isMeteredUsageRole === true;

    // Sync Operations runs data-changing actions, so it is limited to Enterprise Admin and Operations Admin.
    const canSeeSync = useCanUseSync() === true;

    const navItems = NAV_ITEMS.filter(
        (item) =>
            (item.to !== ACCESS_MANAGEMENT_PATH || canSeeAccessManagement) &&
            (!item.needsMeteredUsage || canSeeMeteredUsage) &&
            (!item.needsSync || canSeeSync)
    );

    return (
        <div className="ecr-ops-shell">
            {/* ── Sidebar ── */}
            <aside className={`ecr-ops-sidebar ${collapsed ? "collapsed" : ""}`}>
                <div className="ecr-ops-sidebar__header">
                    {!collapsed && (
                        <button
                            className="ecr-ops-sidebar__brand"
                            onClick={() => navigate("/ecr-ops")}
                            type="button"
                            aria-label="Go to ECR Ops home"
                        >
                            <span className="ecr-ops-sidebar__brand-badge">ECR</span>
                            <span className="ecr-ops-sidebar__brand-label">Ops Portal</span>
                        </button>
                    )}
                    <button
                        className="ecr-ops-sidebar__toggle"
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label="Toggle sidebar"
                    >
                        {collapsed ? <MenuIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                    </button>
                </div>

                <nav className="ecr-ops-sidebar__nav">
                    {navItems.map((item) => {
                        // Special handling for search routes - make active when on any search subroute
                        const isSearchLink = item.to.includes('/search');
                        const isSearchActive = isSearchLink && location.pathname.includes('/search');
                        // Access Management has 3 sub-routes under one nav item - keep it
                        // active for all of them, not just the default role-assignments route.
                        const isAccessManagementLink = item.to.includes('/access-management');
                        const isAccessManagementActive =
                            isAccessManagementLink && location.pathname.includes('/access-management');

                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `ecr-ops-sidebar__link${(isActive || isSearchActive || isAccessManagementActive) ? " active" : ""}`
                                }
                                title={collapsed ? item.label : undefined}
                            >
                                <span className="ecr-ops-sidebar__link-icon">{item.icon}</span>
                                {!collapsed && (
                                    <span className="ecr-ops-sidebar__link-label">{item.label}</span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                {!collapsed && (
                    <div className="ecr-ops-sidebar__footer">
                        <span>ECR Operations</span>
                    </div>
                )}
            </aside>

            {/* ── Main content ── */}
            <main className="ecr-ops-main">
                <div className="ecr-ops-platform-bar">
                    <EcrOpsPlatformSelector />
                </div>
                <Outlet />
            </main>
        </div>
    );
};

export default EcrOpsLayout;
